import { FormEvent, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  ConventionalSelect,
  Form,
  InlineLink,
  SectionAlert,
  Spacer,
  SpinnerInline,
  TextField,
  Txt,
  validators,
} from '@vst/beam';
import { ArrowDownward } from '@vst/beam-icons/icons';
import { PhoneNumberFormat, PhoneNumberUtil } from 'google-libphonenumber';
import { useTranslation } from 'react-i18next';

import { getAvailableNumbers, reserveSelectedNumber, selectAddOns, setVoiceFormValues } from '@app/redux/addOns';
import { selectUserInfo } from '@app/redux/userInfo';
import { getShortenedZipCode } from '@app/utils';

import { TransitionWrapper } from '../TransitionWrapper';
import { Step } from './step';
import { StepInstanceProps } from '../../types';

import styles from '../../styles/configure-voice.module.scss';

export const Step1 = ({ isOpen, openStep, setOpenStep }: StepInstanceProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ShopAddons');
  const {
    userInfo: {
      address: { service },
    },
  } = useSelector(selectUserInfo);
  const {
    voiceConfig: { postalCode },
    availableNumbers,
  } = useSelector(selectAddOns);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isZipCodeOpen, setIsZipCodeOpen] = useState(false);
  const [isValid, setIsValid] = useState(false);

  const handlePhoneNumberSubmit = () => {
    if (!availableNumbers || availableNumbers.length === 0) {
      return;
    }

    setIsValid(true);
    dispatch(reserveSelectedNumber(phoneNumber));
    setOpenStep(openStep + 1);
  };

  const displayedZipCode = getShortenedZipCode((postalCode || service?.postalCode) ?? '');

  return (
    <Step step={1} title={t('voice.phone.title')} isValid={isValid} handleEdit={() => setOpenStep(1)}>
      {!isOpen && (
        <Txt variant="bodyLargeRegular" color="subtle">
          {formatPhoneNumber(phoneNumber)}
        </Txt>
      )}
      <TransitionWrapper isOpen={isOpen}>
        {isOpen && (
          <>
            <SectionAlert variant="infoSecondary" showIcon={false}>
              <div className={styles.stepOneSectionAlert}>
                <Txt variant="smallBold" component="span">
                  {t('voice.hasExisting')}
                </Txt>
                <Txt variant="smallRegular">{t('voice.phone.transfer')}</Txt>
                <InlineLink href={`tel:${t('voice.phone.carePhoneNumber')}`}>
                  {t('voice.phone.carePhoneNumber')}
                </InlineLink>
                <Txt variant="smallRegular">{t('voice.phone.getStarted')}</Txt>
              </div>
            </SectionAlert>
            {isZipCodeOpen ? (
              <ZipCodeForm setIsZipCodeOpen={setIsZipCodeOpen} />
            ) : (
              <div>
                <Spacer y="24px" />
                <Txt variant="linkBodySmall">{t('voice.phone.numbersAvailable')}</Txt>
                <Txt variant="bodySmallBold" component="span" mr="4px">
                  {displayedZipCode}
                </Txt>
                <InlineLink
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsZipCodeOpen(true);
                  }}
                >
                  <Txt variant="smallRegular" component="span">
                    {t('voice.change')}
                  </Txt>
                </InlineLink>
              </div>
            )}
            <Spacer y="24px" />
            <PhoneNumberForm
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              isZipCodeOpen={isZipCodeOpen}
              handleSubmit={handlePhoneNumberSubmit}
            />
          </>
        )}
      </TransitionWrapper>
    </Step>
  );
};

const ZipCodeForm = ({ setIsZipCodeOpen }: { setIsZipCodeOpen: (isOpen: boolean) => void }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('ShopAddons');
  const {
    voiceConfig: { postalCode },
  } = useSelector(selectAddOns);

  const handleCancel = () => {
    setIsZipCodeOpen(false);
  };

  const handleZipCodeSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData: any = new FormData(e.target as HTMLFormElement);
    const formValues = Object.fromEntries(formData.entries());

    dispatch(setVoiceFormValues({ postalCode: formValues.zip }));
    dispatch(getAvailableNumbers(formValues.zip));
    setIsZipCodeOpen(false);
  };

  return (
    <Form
      initialValues={{ zip: postalCode }}
      validationMode="onSubmit"
      onSubmit={handleZipCodeSubmit}
      className={styles.formContent}
    >
      <div style={{ display: 'flex', flexFlow: 'column' }}>
        <Spacer y="24px" />
        <TextField
          fluid
          required
          name="zip"
          defaultValue={postalCode}
          labelProps={{ labelText: t('voice.phone.zipLabel') }}
          validationRules={[
            validators.required({ message: t('voice.validation.required') }),
            validators.pattern({
              pattern: /^[0-9]{5}/,
              message: t('voice.validation.postalCode'),
            }),
            validators.maxLength({
              length: 5,
              message: t('voice.validation.zipCode'),
            }),
          ]}
        />
        <div className={styles['zipCodeButtons']}>
          <Button type="button" variant="tertiary" onClick={handleCancel}>
            {t('voice.cancel')}
          </Button>
          <Button type="submit" variant="secondary">
            {t('voice.save')}
          </Button>
        </div>
      </div>
    </Form>
  );
};

const PhoneNumberForm = ({
  phoneNumber,
  setPhoneNumber,
  isZipCodeOpen,
  handleSubmit,
}: {
  phoneNumber: string;
  setPhoneNumber: (phone: string) => void;
  isZipCodeOpen: boolean;
  handleSubmit: () => void;
}) => {
  const { availableNumbers, availableNumbersLoading, availableNumbersError } = useSelector(selectAddOns);
  const { t } = useTranslation('ShopAddons');

  const noNumbersFound = !availableNumbersLoading && (availableNumbers?.length === 0 || availableNumbersError);

  return (
    <Form
      initialValues={{ phone: phoneNumber }}
      validationMode="onSubmit"
      onSubmit={(e) => {
        e.preventDefault();
        if (phoneNumber) handleSubmit();
      }}
      className={styles.formContent}
    >
      <div className={styles['phoneNumberSelect']}>
        {availableNumbersLoading ? (
          <SpinnerInline label={t('voice.phone.loading')} />
        ) : noNumbersFound ? (
          <SectionAlert label={t('voice.phone.error')} variant={'error'} />
        ) : (
          <ConventionalSelect
            name="phone"
            fluid
            required
            labelProps={{ labelText: t('voice.phone.phoneLabel') }}
            placeholder={availableNumbersLoading ? t('voice.phone.loading') : t('voice.phone.select')}
            options={availableNumbers?.map((entry) => ({
              label: formatPhoneNumber(entry.phoneNumber),
              value: entry.phoneNumber,
            }))}
            validationRules={[
              validators.required({
                message: t('voice.validation.required'),
              }),
            ]}
            value={phoneNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhoneNumber(e.target.value)}
            disabled={isZipCodeOpen}
          />
        )}
      </div>
      <Button type="submit" variant="secondary" icon={ArrowDownward} iconPos="right" className={styles.nextButton}>
        {t('voice.nextStep')}
      </Button>
    </Form>
  );
};

function formatPhoneNumber(phoneNumber: string) {
  try {
    const phoneUtil = PhoneNumberUtil.getInstance();
    const formattedPhoneNumber = phoneUtil.format(phoneUtil.parse(phoneNumber, 'US'), PhoneNumberFormat.NATIONAL);
    return formattedPhoneNumber;
  } catch {
    return phoneNumber;
  }
}
